import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { Calendar, Loader2, CheckCircle2, AlertTriangle, Search, HardHat, Plus, X, User, Shield } from 'lucide-react';

export const ScheduleInstallationDialog = ({ open, onOpenChange, customer, isAdmin, onScheduled, onBookedAndShouldEmail }) => {
  const [duration, setDuration] = useState(1);
  const [fromDate, setFromDate] = useState(new Date().toISOString().slice(0, 10));
  const [skipWeekends, setSkipWeekends] = useState(true);
  const [includeClashes, setIncludeClashes] = useState(false);
  const [searching, setSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [booking, setBooking] = useState(null);
  const [people, setPeople] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showAddEng, setShowAddEng] = useState(false);
  const [newEng, setNewEng] = useState({ name: '', email: '', phone: '' });
  const [creatingEng, setCreatingEng] = useState(false);
  const [sortByLoad, setSortByLoad] = useState(false);
  const [nearby, setNearby] = useState([]);
  const [nearbyLoading, setNearbyLoading] = useState(false);

  const loadPeople = async () => {
    try {
      const r = await api.get('/installation-people');
      setPeople(r.data || []);
    } catch {
      setPeople([]);
    }
  };

  useEffect(() => {
    if (open) loadPeople();
    // Pre-fill duration from the customer's installation_days field so the
    // booking matches the schedule on the customer record (multi-day installs).
    if (open && customer?.installation_days) {
      const d = parseInt(customer.installation_days, 10);
      if (!isNaN(d) && d > 0) setDuration(d);
    }
  }, [open, customer?.installation_days]);

  const toggleId = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const createEngineer = async () => {
    if (!newEng.name.trim() || !newEng.email.trim()) {
      toast.error('Name and email required');
      return;
    }
    setCreatingEng(true);
    try {
      const r = await api.post('/engineers', { name: newEng.name, email: newEng.email, phone: newEng.phone });
      toast.success('Engineer created');
      setNewEng({ name: '', email: '', phone: '' });
      setShowAddEng(false);
      await loadPeople();
      // Auto-select the new one
      if (r.data?.id) setSelectedIds((prev) => [...prev, r.data.id]);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create engineer');
    } finally {
      setCreatingEng(false);
    }
  };

  const search = async () => {
    setSearching(true);
    setResults(null);
    try {
      const r = await api.post('/installations/availability', {
        duration_days: Number(duration),
        from_date: fromDate,
        slots: 6,
        skip_weekends: skipWeekends,
        include_clashes: includeClashes && isAdmin,
      });
      setResults(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  const book = async (slot) => {
    if (!customer) return;
    setBooking(slot.start_date);
    try {
      await api.post('/installations', {
        customer_id: customer.id,
        engineer_ids: selectedIds,
        engineer_id: selectedIds[0] || '',
        title: `Installation — ${customer.first_name} ${customer.last_name}`,
        description: `Scheduled via availability finder (${slot.duration_days} day${slot.duration_days > 1 ? 's' : ''})${slot.clash ? ' — admin override, alongside existing bookings' : ''}`,
        scheduled_date: slot.start_date,
        end_date: slot.end_date,
        start_time: '09:00',
        end_time: '17:00',
        installation_type: 'solar',
      });
      toast.success(slot.clash ? 'Scheduled alongside existing bookings' : 'Installation scheduled');
      onOpenChange(false);
      setResults(null);
      if (onScheduled) onScheduled();
      // Prompt parent to open the customer email dialog with the
      // installation-scheduled template so admin can confirm it with the customer.
      if (onBookedAndShouldEmail) {
        onBookedAndShouldEmail({
          start_date: slot.start_date,
          end_date: slot.end_date,
          duration_days: slot.duration_days,
          engineer_ids: selectedIds,
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Scheduling failed');
    } finally {
      setBooking(null);
    }
  };

  const formatRange = (s) => {
    if (s.start_date === s.end_date) return s.start_date;
    return `${s.start_date} → ${s.end_date}`;
  };

  const selectedBusyOnSlot = (slot) => {
    const busy = new Set(slot.busy_engineer_ids || []);
    return selectedIds.filter((id) => busy.has(id))
      .map((id) => people.find((p) => p.id === id)?.name || id);
  };

  const sortedPeople = sortByLoad
    ? [...people].sort((a, b) => (a.job_count_30d || 0) - (b.job_count_30d || 0))
    : people;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="schedule-install-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            Schedule Installation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Duration (days)</Label>
              <Input type="number" min="1" max="30" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="duration-input" />
            </div>
            <div>
              <Label>Search from</Label>
              <Input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="from-date-input" />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="weekends">Skip weekends</Label>
            <Switch id="weekends" checked={skipWeekends} onCheckedChange={setSkipWeekends} data-testid="weekends-switch" />
          </div>
          {isAdmin && (
            <div className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded">
              <div>
                <Label htmlFor="clashes" className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  Admin override: show clashing dates too
                </Label>
                <p className="text-xs text-slate-500 mt-1">Schedules alongside existing bookings without deleting them.</p>
              </div>
              <Switch id="clashes" checked={includeClashes} onCheckedChange={setIncludeClashes} data-testid="clashes-switch" />
            </div>
          )}

          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-2">
                <HardHat className="w-4 h-4 text-blue-600" />
                Assign engineers / staff (optional, multiple allowed)
              </Label>
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-1 cursor-pointer text-xs text-slate-600" title="Show least-busy people first">
                  <input
                    type="checkbox"
                    checked={sortByLoad}
                    onChange={(e) => setSortByLoad(e.target.checked)}
                    className="w-3 h-3"
                    data-testid="sort-by-load"
                  />
                  Sort by load
                </label>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => setShowAddEng((v) => !v)}
                  className="text-xs h-7"
                  data-testid="toggle-add-engineer-btn"
                >
                  {showAddEng ? <><X className="w-3 h-3 mr-1" /> Cancel</> : <><Plus className="w-3 h-3 mr-1" /> Add new engineer</>}
                </Button>
              </div>
            </div>
            {showAddEng && (
              <div className="grid grid-cols-3 gap-2 mb-2 p-3 border border-blue-200 bg-blue-50 rounded">
                <Input placeholder="Name *" value={newEng.name} onChange={(e) => setNewEng({ ...newEng, name: e.target.value })} data-testid="new-eng-name" />
                <Input placeholder="Email *" type="email" value={newEng.email} onChange={(e) => setNewEng({ ...newEng, email: e.target.value })} data-testid="new-eng-email" />
                <div className="flex gap-2">
                  <Input placeholder="Phone" value={newEng.phone} onChange={(e) => setNewEng({ ...newEng, phone: e.target.value })} data-testid="new-eng-phone" />
                  <Button type="button" onClick={createEngineer} disabled={creatingEng} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="save-new-eng-btn">
                    {creatingEng ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Save'}
                  </Button>
                </div>
              </div>
            )}
            {people.length === 0 ? (
              <p className="text-xs text-slate-400">No engineers or staff available.</p>
            ) : (
              <div className="grid grid-cols-2 gap-2 p-3 border border-slate-200 rounded-md bg-slate-50 max-h-[200px] overflow-y-auto">
                {sortedPeople.map((p) => {
                  const Icon = p.type === 'engineer' ? HardHat : (p.type === 'admin' ? Shield : User);
                  const jobs = p.job_count_30d || 0;
                  const loadColor = jobs === 0 ? 'text-slate-400' : jobs < 3 ? 'text-emerald-600' : jobs < 6 ? 'text-amber-600' : 'text-rose-600';
                  return (
                    <label
                      key={p.id}
                      className="flex items-center gap-2 cursor-pointer hover:bg-white px-2 py-1 rounded"
                      data-testid={`person-option-${p.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(p.id)}
                        onChange={() => toggleId(p.id)}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <Icon className="w-3 h-3" style={{ color: p.color }} />
                      <span className="text-sm text-slate-700 truncate">{p.name}</span>
                      <span className={`text-[10px] font-medium ml-auto ${loadColor}`} title={`${jobs} job${jobs === 1 ? '' : 's'} in next 30 days`}>
                        {jobs} job{jobs === 1 ? '' : 's'}
                      </span>
                      <Badge variant="outline" className="text-[9px] px-1">
                        {p.type === 'engineer' ? 'Eng' : (p.type === 'admin' ? 'Admin' : 'Staff')}
                      </Badge>
                    </label>
                  );
                })}
              </div>
            )}
            {selectedIds.length > 0 && (
              <p className="text-xs text-blue-600 mt-1">
                {selectedIds.length} {selectedIds.length === 1 ? 'person' : 'people'} will be assigned to this installation.
              </p>
            )}
          </div>

          <Button onClick={search} disabled={searching} className="w-full" data-testid="find-slots-btn">
            {searching ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Search className="w-4 h-4 mr-2" />}
            Find available dates
          </Button>

          {results && (
            <div className="space-y-3 max-h-[280px] overflow-y-auto">
              {results.clean.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-emerald-700 uppercase flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3" /> Free dates
                  </div>
                  {results.clean.map((s) => (
                    <div key={s.start_date} className="flex items-center justify-between p-3 border border-emerald-200 bg-emerald-50 rounded" data-testid={`free-slot-${s.start_date}`}>
                      <span className="text-sm font-medium">{formatRange(s)}</span>
                      <Button size="sm" onClick={() => book(s)} disabled={booking === s.start_date} className="bg-emerald-600 hover:bg-emerald-700 text-white">
                        {booking === s.start_date ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Book'}
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              {results.clashes && results.clashes.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs font-semibold text-amber-700 uppercase flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> Clashing dates (admin override)
                  </div>
                  {results.clashes.map((s) => {
                    const engineerClashes = selectedBusyOnSlot(s);
                    return (
                      <div key={s.start_date} className="p-3 border border-amber-200 bg-amber-50 rounded" data-testid={`clash-slot-${s.start_date}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{formatRange(s)}</span>
                          <Button size="sm" variant="outline" onClick={() => book(s)} disabled={booking === s.start_date} className="border-amber-400 text-amber-700 hover:bg-amber-100">
                            {booking === s.start_date ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Book alongside'}
                          </Button>
                        </div>
                        <div className="mt-1 text-xs text-slate-600">
                          Clashes with: {s.clashing_installations.map((c) => c.customer_name || c.title).join(', ')}
                        </div>
                        {engineerClashes.length > 0 && (
                          <div className="mt-1 text-xs flex items-center gap-1 text-rose-700 font-medium" data-testid={`engineer-clash-${s.start_date}`}>
                            <AlertTriangle className="w-3 h-3" />
                            Selected: {engineerClashes.join(', ')} already booked on these dates
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
              {results.clean.length === 0 && (!results.clashes || results.clashes.length === 0) && (
                <div className="text-center py-4 text-slate-400 text-sm">No dates found in the next 180 days. {!includeClashes && isAdmin ? 'Try enabling admin override to see clashing slots.' : ''}</div>
              )}
            </div>
          )}

          {Number(duration) <= 1 && (nearby.length > 0 || nearbyLoading) && (
            <div className="border-t pt-3" data-testid="nearby-panel">
              <Label className="flex items-center gap-2 mb-2">
                <Search className="w-4 h-4 text-emerald-600" />
                Nearby jobs you could batch on the same day
              </Label>
              <p className="text-[11px] text-slate-500 mb-2">
                Other customers awaiting scheduling near {customer?.postcode || '—'} (same area). Open their record in a new tab to schedule them on the same date.
              </p>
              {nearbyLoading ? (
                <div className="text-center py-3"><Loader2 className="w-4 h-4 animate-spin inline text-emerald-500" /></div>
              ) : (
                <div className="space-y-1 max-h-[160px] overflow-y-auto">
                  {nearby.map((n) => (
                    <a
                      key={n.id}
                      href={`/customers/${n.id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-between p-2 border border-slate-200 rounded hover:bg-emerald-50 hover:border-emerald-300 transition"
                      data-testid={`nearby-${n.id}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-900 truncate">
                          {n.first_name} {n.last_name}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {n.address}{n.city ? `, ${n.city}` : ''} {n.postcode}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge variant="outline" className="text-[9px]">
                          {n.proximity === 'same_outward' ? '~0–3 mi' : '~5–25 mi'}
                        </Badge>
                        <Badge className="bg-blue-100 text-blue-700 text-[9px]">
                          {n.installation_days || 1}d
                        </Badge>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
